import { useState } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import type { HwCatalogueDevice } from '@/api/hardwareCatalogue';
import {
  createStockItem,
  getStockItemAccessories,
  getStockItemDevices,
  putStockItemAccessories,
  putStockItemDevices,
} from '@/api/stock';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { toneClass } from '@/lib/hardwareRequest';
import type { AccessoryLink, StockItem } from '@/types/oscar';
import type { HwCatalogueAccessory } from '@/api/hardwareCatalogue';
import {
  accessoryLinesFor,
  accessoryNamesFor,
  accessoryReading,
  linkableAccessoryLines,
} from './accessoryStock';
import {
  linesFor,
  linkableLines,
  nextDeviceSet,
  stockReading,
  type StockReading,
  type StockTone,
} from './deviceStock';

/** A stock-holding place, as the warehouse picker needs it. */
export interface Warehouse {
  LocationId: number;
  LocationDesc: string;
}

/** The page's own tone scale, so this reads the same as every other badge in Oscar. */
const TONE: Record<StockTone, keyof typeof toneClass> = {
  none: 'unknown',
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
};

/**
 * The one-line stock reading beside a device (see {@link stockReading}).
 *
 * ⚠ The hint is a tooltip AND the text a screen reader gets, because "Not
 * stocked" on its own reads as a fault and the whole point of the wording is
 * that it is not.
 */
export function StockBadge({ device }: { device: HwCatalogueDevice }) {
  return <ReadingBadge reading={stockReading(device)} />;
}

/**
 * The same badge for an ACCESSORY (oscar migration 124), in the same words - so
 * "Not stocked" means one thing on both lists, and means "bought per request".
 */
export function AccessoryStockBadge({ lines }: { lines: StockItem[] }) {
  return <ReadingBadge reading={accessoryReading(lines)} />;
}

function ReadingBadge({ reading: r }: { reading: StockReading }) {
  return (
    <span
      title={r.hint}
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${toneClass[TONE[r.tone]]}`}
    >
      {r.label}
    </span>
  );
}

/**
 * Raised when the stock line WAS created and linking it was what failed.
 *
 * ⛔ SAYS WHAT HAPPENED. Re-running the whole thing would create a second line,
 * so the message names the one that exists and where to link it - the same
 * principle the new-device flow follows when its second write fails.
 */
class LinkFailedAfterCreate extends Error {}

/**
 * WHAT ONE SUBJECT - a device or an accessory - NEEDS THE PANEL TO KNOW.
 *
 * ⛔ ONE PANEL, TWO KINDS (oscar migration 124). Anthony, 15/10/2026: *"worked -
 * now do accessories stock link."* The device panel already did everything an
 * accessory needs - show the lines per warehouse, link one, unlink one, add one -
 * so the accessory version is the same panel handed different data, not a copy
 * that would drift from it.
 */
export interface LinkSubject {
  kind: 'device' | 'accessory';
  id: number;
  name: string;
  /** The stock lines that answer it. */
  lines: StockItem[];
  /** The lines that could be linked to it. */
  linkable: StockItem[];
  reading: StockReading;
  totalOnHand: number;
  /**
   * A DEVICE's own "hold at least" (080). ⚠ Undefined for an accessory: it has no
   * company-wide threshold and is Low on each line's own `MinQty`.
   */
  holdAtLeast?: number | null;
  /** The "no device needed" option, which is not a thing on a shelf. */
  holdsNoStock?: boolean;
  /** The names a line answers, for the picker and the "answers nothing" warning. */
  answers: (line: StockItem) => string[];
}

/** HOW A SUBJECT'S LINK IS READ, WRITTEN AND REFRESHED. */
export interface LinkWiring {
  /** The type of stock line that can answer this kind - and the type a new line is created as. */
  itemType: 'Device' | 'Accessory';
  /** The ids a line answers RIGHT NOW. */
  read: (stockItemId: number) => Promise<number[]>;
  /** Replace the whole set a line answers. */
  write: (stockItemId: number, ids: number[]) => Promise<unknown>;
  /** What to re-read after a change, so every badge and list stays true. */
  invalidate: QueryKey[];
}

export function StockLinkPanel({
  subject,
  wiring,
  warehouses,
  canEdit,
}: {
  subject: LinkSubject;
  wiring: LinkWiring;
  warehouses: Warehouse[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkChoice, setLinkChoice] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [unlinking, setUnlinking] = useState<StockItem | null>(null);

  const mine = subject.lines;
  const linkable = subject.linkable;
  const reading = subject.reading;
  const warehouseName = (id: number | null) =>
    id === null ? 'No warehouse' : (warehouses.find((w) => w.LocationId === id)?.LocationDesc ?? 'Unknown warehouse');

  const refresh = () => {
    // The stock list, and whatever else carries this subject's total and line
    // count - the catalogue for a device, the links for an accessory - or the
    // badge beside this panel goes on saying the old one.
    for (const queryKey of wiring.invalidate) void queryClient.invalidateQueries({ queryKey });
  };

  const relink = useMutation({
    // ⛔ THE CURRENT SET IS READ JUST BEFORE IT IS REPLACED. The API replaces the
    // whole set, and the list this page holds may be minutes old: sending a set
    // built from it would undo whatever somebody changed on the Stock page since.
    mutationFn: async ({ line, action }: { line: StockItem; action: 'link' | 'unlink' }) => {
      const fresh = await wiring.read(line.StockItemId);
      await wiring.write(line.StockItemId, nextDeviceSet(fresh, subject.id, action));
    },
    onSuccess: () => {
      setError(null);
      setLinkOpen(false);
      setLinkChoice('');
      setUnlinking(null);
      refresh();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not change what this stock line answers.'),
  });

  const add = useMutation({
    mutationFn: async (draft: { name: string; locationId: number; qty: number; serialised: boolean }) => {
      const item = await createStockItem({
        Name: draft.name,
        ItemType: wiring.itemType,
        Category: null,
        MinQty: null,
        UnitCost: null,
        LocationId: draft.locationId,
        InitialQty: draft.qty,
        IsSerialised: draft.serialised,
      });

      try {
        await wiring.write(item.StockItemId, [subject.id]);
      } catch {
        throw new LinkFailedAfterCreate(
          `The stock line “${draft.name}” was created, but linking it to ${subject.name} failed. `
          + 'It will not be offered to a request until it is linked - use “Link an existing line” '
          + 'here, or the Stock page.',
        );
      }
    },
    onSuccess: () => {
      setError(null);
      setAddOpen(false);
      refresh();
    },
    onError: (e: unknown) => {
      setError(
        e instanceof ApiError || e instanceof LinkFailedAfterCreate
          ? e.message
          : 'Could not add the stock line.',
      );
      // ⚠ The line may exist now. Refresh so it is not shown as missing.
      if (e instanceof LinkFailedAfterCreate) refresh();
    },
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4">
      <h3 className="font-heading text-sm font-bold text-navy">What we hold for {subject.name}</h3>

      {subject.holdsNoStock ? (
        <p className="mt-2 text-sm text-darkGrey">
          This is the &ldquo;no device needed&rdquo; option. It is not a thing on a shelf, so it
          holds no stock.
        </p>
      ) : (
        <>
          {mine.length === 0 ? (
            <p className="mt-2 text-sm text-darkGrey">
              Nothing on the Stock page answers this {subject.kind}, so a request for it is{' '}
              <strong>fulfilled by purchase</strong>. That is normal for something bought per
              request &mdash; {subject.kind === 'device'
                ? 'an iPhone from the telco, a label printer'
                : 'a case or a charger bought as needed'}.
            </p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table aria-label={`Stock lines for ${subject.name}`} className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
                    <th className="py-1.5 pr-3">Stock line</th>
                    <th className="py-1.5 pr-3">Warehouse</th>
                    <th className="py-1.5 pr-3">On hand</th>
                    <th className="py-1.5 pr-3">Counted by</th>
                    {canEdit && <th className="py-1.5"><span className="sr-only">Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {mine.map((l) => (
                    <tr key={l.StockItemId} className="border-b border-slate-200/70">
                      <td className="py-1.5 pr-3 font-medium text-darkGrey">{l.Name}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{warehouseName(l.LocationId)}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{l.QtyOnHand}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{l.IsSerialised ? 'Serial number' : 'Count'}</td>
                      {canEdit && (
                        <td className="py-1.5 text-right">
                          <button
                            type="button"
                            aria-label={`Unlink ${l.Name} from ${subject.name}`}
                            className="text-xs font-semibold text-navy hover:underline"
                            onClick={() => {
                              setError(null);
                              setUnlinking(l);
                            }}
                          >
                            Unlink
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ⚠ A DEVICE: the company total against THIS device's own threshold - the
              number that decides Low, and the one the lines above add up to.
              AN ACCESSORY has no company-wide threshold: each line is Low on its
              own minimum, so it says that instead of inventing a number. */}
          <p className="mt-2 text-xs text-slate-600">
            {subject.kind === 'device' ? 'Company total' : 'Total on hand'} <strong>{subject.totalOnHand}</strong>
            {subject.holdAtLeast != null && <> against <strong>hold at least {subject.holdAtLeast}</strong></>}
            {mine.length > 0 && <> &middot; <span title={reading.hint}>{reading.label}</span></>}
            {subject.kind === 'accessory' && mine.length > 0 && <>. Each line is Low against its own minimum</>}
            . Counts and movements are on the Stock page.
          </p>

          {canEdit && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={linkable.length === 0}
                title={linkable.length === 0
                  ? `Every ${wiring.itemType.toLowerCase()}-type stock line already answers this ${subject.kind}.`
                  : undefined}
                className="rounded border border-navy bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-bg disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setError(null);
                  setLinkChoice('');
                  setLinkOpen(true);
                }}
              >
                Link an existing line…
              </button>
              <button
                type="button"
                className="rounded border border-navy bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-bg"
                onClick={() => {
                  setError(null);
                  setAddOpen(true);
                }}
              >
                Add a stock line…
              </button>
            </div>
          )}
        </>
      )}

      {error && !linkOpen && !addOpen && !unlinking && (
        <p role="alert" className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
          {error}
        </p>
      )}

      {/* ── link an existing line ─────────────────────────────────────────── */}
      <AlertDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        title={`Link a stock line to ${subject.name}`}
        description={
          <>
            Choose a stock line that should answer <strong>{subject.name}</strong>. A request for it
            will then be offered that line. It keeps any other {subject.kind} it already answers.
          </>
        }
        confirmLabel={relink.isPending ? 'Linking…' : 'Link'}
        confirmDisabled={linkChoice === '' || relink.isPending}
        closeOnConfirm={false}
        onConfirm={() => {
          const line = linkable.find((l) => String(l.StockItemId) === linkChoice);
          if (line) relink.mutate({ line, action: 'link' });
        }}
      >
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Stock line
          </span>
          <select className="input" value={linkChoice} onChange={(e) => setLinkChoice(e.target.value)}>
            <option value="">Select…</option>
            {linkable.map((l) => (
              <option key={l.StockItemId} value={l.StockItemId}>
                {l.Name} — {warehouseName(l.LocationId)} · {l.QtyOnHand} on hand
                {subject.answers(l).length === 0
                  ? ' · answers nothing yet'
                  : ` · answers ${subject.answers(l).join(', ')}`}
              </option>
            ))}
          </select>
        </label>
        {error && (
          <p role="alert" className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>

      {/* ── unlink ────────────────────────────────────────────────────────── */}
      <AlertDialog
        open={unlinking !== null}
        onOpenChange={(open) => { if (!open) setUnlinking(null); }}
        title={`Stop ${unlinking?.Name ?? 'this line'} answering ${subject.name}?`}
        description={
          <>
            The stock line stays on the Stock page and keeps its count. A request for{' '}
            <strong>{subject.name}</strong> will no longer be offered it.
            {unlinking && subject.answers(unlinking).length === 1 && (
              <> ⚠ It answers nothing else, so it will answer <strong>no {subject.kind}</strong> afterwards
              and cannot be given to any request from stock.</>
            )}
          </>
        }
        confirmLabel={relink.isPending ? 'Unlinking…' : 'Unlink'}
        confirmDisabled={relink.isPending}
        closeOnConfirm={false}
        onConfirm={() => { if (unlinking) relink.mutate({ line: unlinking, action: 'unlink' }); }}
      >
        {error && (
          <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>

      {/* ── add a stock line ──────────────────────────────────────────────── */}
      <AddLineDialog
        key={addOpen ? 'open' : 'closed'}
        open={addOpen}
        onOpenChange={setAddOpen}
        subject={subject}
        itemType={wiring.itemType}
        warehouses={warehouses}
        pending={add.isPending}
        error={error}
        onAdd={(draft) => add.mutate(draft)}
      />
    </div>
  );
}

/**
 * Add a stock line for this device, already linked to it.
 *
 * ⛔ THE NAME IS TYPED ONCE. It starts as the device's own name - the same
 * "stock line and catalogue device are two records, but do not type the name
 * twice" rule the new-device flow follows - and can be changed, because the
 * warehouse often names a spec tier ("Laptop (High Spec)") where a requester
 * says "Laptop".
 *
 * ⚠ THE WAREHOUSE IS REQUIRED. A count nobody can go and fetch is the thing the
 * stock API refuses anyway; refusing it here means it is said before anything is
 * created.
 *
 * ⚠ Remounted on every open (the `key` at the call site), so a cancelled draft
 * is not silently resurrected the next time somebody adds a line.
 */
function AddLineDialog({
  open,
  onOpenChange,
  subject,
  itemType,
  warehouses,
  pending,
  error,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: LinkSubject;
  itemType: 'Device' | 'Accessory';
  warehouses: Warehouse[];
  pending: boolean;
  error: string | null;
  onAdd: (draft: { name: string; locationId: number; qty: number; serialised: boolean }) => void;
}) {
  const [name, setName] = useState(subject.name);
  const [warehouse, setWarehouse] = useState('');
  // ⚠ Held as TEXT. A number box's empty value is '' and `Number('')` is 0, so
  // an untouched box would silently become "opening quantity: 0" - which here
  // happens to be the right default, but only by accident.
  const [qty, setQty] = useState('0');
  const [serialised, setSerialised] = useState(false);

  const qtyNumber = qty.trim() === '' ? 0 : Number(qty);
  const qtyValid = Number.isInteger(qtyNumber) && qtyNumber >= 0;
  const valid = name.trim() !== '' && warehouse !== '' && qtyValid;

  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Add a stock line for ${subject.name}`}
      description={`Creates a ${itemType.toLowerCase()} stock line already set to answer this ${subject.kind}.`}
      confirmLabel={pending ? 'Adding…' : 'Add'}
      confirmDisabled={!valid || pending}
      closeOnConfirm={false}
      onConfirm={() =>
        onAdd({ name: name.trim(), locationId: Number(warehouse), qty: qtyNumber, serialised })}
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Warehouse</span>
          <select className="input" value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
            <option value="">Select…</option>
            {warehouses.map((w) => (
              <option key={w.LocationId} value={w.LocationId}>{w.LocationDesc}</option>
            ))}
          </select>
        </label>

        <div>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Opening quantity
            </span>
            <input
              type="number"
              min={0}
              step={1}
              className="input"
              value={qty}
              aria-invalid={!qtyValid}
              aria-describedby={qtyValid ? undefined : 'add-line-qty-error'}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          {/* ⚠ OUTSIDE THE LABEL, and tied to the box by aria-describedby. Inside
              it, the message became part of the field's accessible NAME - so the
              box was called "Opening quantity A whole number, nought or more"
              the moment it was wrong, and nothing could find it by its label. */}
          {!qtyValid && (
            <span id="add-line-qty-error" className="mt-1 block text-xs text-red">
              A whole number, nought or more.
            </span>
          )}
        </div>

        <label className="flex items-start gap-2 text-sm text-darkGrey">
          <input
            type="checkbox"
            className="mt-1"
            checked={serialised}
            onChange={(e) => setSerialised(e.target.checked)}
          />
          <span>
            <span className="font-semibold">Counted by serial number</span>
            <span className="block text-xs text-slate-600">
              Each unit is an individual machine. A request allocates these one at a time, and
              issuing one asks which machine went out.
            </span>
          </span>
        </label>

        <p className="text-xs text-slate-600">
          One warehouse. The same {subject.kind} at a second warehouse is a second stock line, added the
          same way.
        </p>

        {error && (
          <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </div>
    </AlertDialog>
  );
}

/**
 * What we hold for ONE DEVICE, per warehouse - the device-centric view.
 *
 * Anthony, 14/10/2026: *"the team is confused about the difference of hardware
 * catalogue and stock - we should link them/consolidate or make it easier to
 * understand."* He chose one screen where each device shows its stock lines.
 *
 * ⛔ THE LINK IS EDITABLE FROM HERE, NOT ONLY READABLE. It could be seen and
 * changed only on the Stock page, and from the catalogue only when a device was
 * first created.
 *
 * ⚠ ADMIN ONLY, as the mapping is (`PUT /api/stock-items/{id}/devices`).
 */
export function DeviceStockPanel({
  device,
  items,
  warehouses,
  canEdit,
}: {
  device: HwCatalogueDevice;
  items: StockItem[];
  warehouses: Warehouse[];
  canEdit: boolean;
}) {
  const subject: LinkSubject = {
    kind: 'device',
    id: device.DeviceOptionId,
    name: device.Name,
    lines: linesFor(device.DeviceOptionId, items),
    linkable: linkableLines(device.DeviceOptionId, items),
    reading: stockReading(device),
    totalOnHand: device.TotalOnHand,
    holdAtLeast: device.MinQty,
    holdsNoStock: device.IsAccessoryOnly,
    answers: (l) => l.Devices.map((d) => d.DeviceName),
  };

  const wiring: LinkWiring = {
    itemType: 'Device',
    read: async (id) => (await getStockItemDevices(id)).DeviceOptionIds,
    write: (id, ids) => putStockItemDevices(id, ids),
    // The catalogue carries each device's company total and line count.
    invalidate: [['stock-items'], ['hardware-catalogue']],
  };

  return <StockLinkPanel subject={subject} wiring={wiring} warehouses={warehouses} canEdit={canEdit} />;
}

/**
 * What we hold for ONE ACCESSORY, per warehouse (oscar migration 124).
 *
 * Anthony, 15/10/2026: *"worked - now do accessories stock link."* The same panel
 * as a device gets, handed the accessory's data - so a requested headset or case
 * can be seen against the shelf and allocated like anything else.
 *
 * ⛔ DERIVED FROM THE LINKS, which arrive as their own list of pairs; and an
 * accessory has no company-wide threshold, so the panel shows none.
 *
 * ⚠ ADMIN ONLY, as the mapping is (`PUT /api/stock-items/{id}/accessories`), and
 * only an accessory-TYPE line can answer an accessory - the server refuses any
 * other, so the picker does not offer it.
 */
export function AccessoryStockPanel({
  accessory,
  accessories,
  items,
  links,
  warehouses,
  canEdit,
}: {
  accessory: HwCatalogueAccessory;
  /** Every accessory, for naming what a line already answers. */
  accessories: HwCatalogueAccessory[];
  items: StockItem[];
  links: AccessoryLink[];
  warehouses: Warehouse[];
  canEdit: boolean;
}) {
  const names = new Map(accessories.map((a) => [a.AccessoryOptionId, a.Name]));
  const lines = accessoryLinesFor(accessory.AccessoryOptionId, items, links);

  const subject: LinkSubject = {
    kind: 'accessory',
    id: accessory.AccessoryOptionId,
    name: accessory.Name,
    lines,
    linkable: linkableAccessoryLines(accessory.AccessoryOptionId, items, links),
    reading: accessoryReading(lines),
    totalOnHand: lines.reduce((sum, l) => sum + l.QtyOnHand, 0),
    answers: (l) => accessoryNamesFor(l.StockItemId, links, names),
  };

  const wiring: LinkWiring = {
    itemType: 'Accessory',
    read: async (id) => (await getStockItemAccessories(id)).AccessoryOptionIds,
    write: (id, ids) => putStockItemAccessories(id, ids),
    invalidate: [['stock-items'], ['stock-accessory-links']],
  };

  return <StockLinkPanel subject={subject} wiring={wiring} warehouses={warehouses} canEdit={canEdit} />;
}
